import React, { useEffect, createRef } from 'react';
import { useDispatch } from 'react-redux';
import DatePicker from 'react-datepicker';
import { RenderErrors } from '../ui/ErrorComponent';
import { getTranslator } from '../../Translator';
import { SidePanel } from '../ui/SidePanel';
import { editNextElectionName, makeNextElection, nextCandidacyFromSet, nextCandidacyUntilSet, nextElectionCodeSet, nextVisibleFromSet, nextVisibleUntilSet, nextVoteCloseSet, nextVoteOpenSet, createElectionSidePanelToggled, systemSet } from '../../features/home/homeSlice';
import moment from 'moment';
import { UnknownAction } from 'redux';
import { useAppSelector } from '../../hooks';

type CreateElectionPaneProps = {
    visible: boolean;
}

export const CreateElectionPane = (props: CreateElectionPaneProps) => {
    const language = useAppSelector(state => state.language);
    const homeScreenState = useAppSelector(state => state.homeScreen);
    const elections = useAppSelector(state => state.elections);
    const token = useAppSelector(state => state.clientState.RequestVerificationToken);

    const t = getTranslator(language);
    const dispatch = useDispatch();
    const hasCandidacy = useAppSelector(state => state.clientState.HasCandidacy);
    const justDutch = useAppSelector(state => state.clientState.JustDutch);

    useEffect(() => {
        if (props.visible) {
            setFocusOnNameNl();
        }
    }, [props.visible])

    const nameNlInputRef = createRef<HTMLInputElement>();

    const setFocusOnNameNl = () => {
        nameNlInputRef && nameNlInputRef.current && nameNlInputRef.current.focus();
    }

    const handleDateChange = (date: Date | null, action: (dateISOString: string) => UnknownAction) => {
        if (date == null) {
            throw new Error("Date should not be null");
        }
        dispatch(action(date.toISOString()));
    };

    return <SidePanel visible={props.visible} toggleAction={() => dispatch(createElectionSidePanelToggled())}>
        <h1>{t['Add elections']}</h1>
        <form className="editor" onSubmit={e => e.preventDefault()}>
            <label>
                {t['Name (NL)']}
                <input type="text" lang="nl-NL" value={homeScreenState.nextName.NL} onChange={e => editNextElectionName({ NL: e.target.value }, homeScreenState, elections, dispatch)} ref={nameNlInputRef} />
            </label>
            {!justDutch && <label>
                {t['Name (EN)']}
                <input type="text" lang="en-GB" value={homeScreenState.nextName.EN} onChange={e => editNextElectionName({ EN: e.target.value }, homeScreenState, elections, dispatch)} />
            </label>}
            <label>
                <input type="radio" checked={!homeScreenState.useCandidateSystem} onChange={e => dispatch(systemSet(!e.target.checked))} />
                {t['Party System']}
            </label>
            <label>
                <input type="radio" checked={homeScreenState.useCandidateSystem} onChange={e => dispatch(systemSet(e.target.checked))} />
                {t['Candidates System']}
            </label>
            <label>
                {t['Election Code']}
                <input maxLength={50} minLength={1} value={homeScreenState.nextCode} onChange={e => dispatch(nextElectionCodeSet({ code: e.target.value, isManual: true }))} />
            </label>
            {hasCandidacy && <>
                <label>
                    {t['Candidacy open']}:
                    <DatePicker
                        dateFormat={t['MM/dd/yyyy HH:mm']}
                        selected={moment(homeScreenState.nextCandidacyFrom).toDate()}
                        onChange={date => handleDateChange(date, nextCandidacyFromSet)}
                        showTimeSelect
                        popperPlacement="right" />
                </label>
                <label>
                    {t['Candidacy closed']}:
                    <DatePicker
                        dateFormat={t['MM/dd/yyyy HH:mm']}
                        selected={moment(homeScreenState.nextCandidacyUntil).toDate()}
                        selectsStart
                        startDate={moment(homeScreenState.nextCandidacyUntil).toDate()}
                        endDate={moment(homeScreenState.nextVisibleFrom).toDate()}
                        onChange={date => handleDateChange(date, nextCandidacyUntilSet)}
                        showTimeSelect
                        popperPlacement="right" />
                </label>
            </>}
            <label>
                {t['Visible from date']}:
                <DatePicker
                    dateFormat={t['MM/dd/yyyy HH:mm']}
                    selected={moment(homeScreenState.nextVisibleFrom).toDate()}
                    selectsStart
                    startDate={moment(homeScreenState.nextVisibleFrom).toDate()}
                    endDate={moment(homeScreenState.nextVisibleUntil).toDate()}
                    onChange={date => handleDateChange(date, nextVisibleFromSet)}
                    showTimeSelect
                    popperPlacement="right" />
            </label>
            <label>
                {t['Open date']}:
                <DatePicker
                    dateFormat={t['MM/dd/yyyy HH:mm']}
                    selected={moment(homeScreenState.nextVoteOpen).toDate()}
                    selectsStart
                    startDate={moment(homeScreenState.nextVoteOpen).toDate()}
                    endDate={moment(homeScreenState.nextVoteClose).toDate()}
                    onChange={date => handleDateChange(date, nextVoteOpenSet)}
                    showTimeSelect
                    popperPlacement="right" />
            </label>
            <label>
                {t['Closing date']}:
                <DatePicker
                    dateFormat={t['MM/dd/yyyy HH:mm']}
                    selected={moment(homeScreenState.nextVoteClose).toDate()}
                    selectsEnd
                    startDate={moment(homeScreenState.nextVoteOpen).toDate()}
                    endDate={moment(homeScreenState.nextVoteClose).toDate()}
                    onChange={date => handleDateChange(date, nextVoteCloseSet)}
                    showTimeSelect
                    popperPlacement="right" />
            </label>
            <label>
                {t['Visible until date']}:
                <DatePicker
                    dateFormat={t['MM/dd/yyyy HH:mm']}
                    selected={moment(homeScreenState.nextVisibleUntil).toDate()}
                    selectsEnd
                    startDate={moment(homeScreenState.nextVisibleFrom).toDate()}
                    endDate={moment(homeScreenState.nextVisibleUntil).toDate()}
                    onChange={date => handleDateChange(date, nextVisibleUntilSet)}
                    showTimeSelect
                    popperPlacement="right" />
            </label>
            <div className="buttonrow">
                <button onClick={() => makeNextElection(homeScreenState, elections, token, dispatch).then((gelukt: boolean) => gelukt && setFocusOnNameNl())}>{t['Create']}</button>
            </div>
        </form>
        <div className="home__error">
            <RenderErrors errors={homeScreenState.errors} />
        </div>
    </SidePanel>
}
